import type { IUser } from '~/entities/user/user/interfaces/user'

import type { ITimeline, TimelineStatus } from '~/features-shared/timeline/interfaces/timeline'

interface GetTemporaryTimelineMessageOptions {
  /** The message author. */
  author: IUser
  /** Id of the current timeline. */
  currentTimelineId: string
  /** Id of the external app. */
  external_app_id: '3DSWYM' | 'RTC'
  /** Temporary message. */
  message: string
  /** The message uri. */
  message_uri: string
  /** Related parent message - optional. */
  parent_comment?: ITimeline
  /** Timeline status. */
  status: TimelineStatus
}

/**
 * Utility method to get a temporary timeline message.
 *
 * @param options.currentTimelineId Id of the current timeline.
 * @param options.message_uri The message uri.
 * @param options.author The message author.
 * @param options.message Temporary message.
 * @param options.parent_comment Related parent message - optional.
 * @returns Temporary timeline message.
 */
export async function getTemporaryTimelineMessage(
  options: GetTemporaryTimelineMessageOptions,
): Promise<Partial<ITimeline>> {
  const item: Partial<ITimeline> = {
    status: options.status,
    community_type: 'dm',
    message_type: 'INSTANT_MESSAGE',
    message_uri: options.message_uri,
    subject_uri: '',
    subject_state: 'ACTIVE',
    external_app_id: options.external_app_id,
    author: options.author,
    message_details: {
      content: options.message,
      moderation_state: 'notModerated',
      parent_message: {
        subject_uri: options.parent_comment?.subject_uri,
        author: options.parent_comment?.author,
        message_details: { content: options.parent_comment?.message_details.content },
      },
      forwarded_from: null,
    },
    from_index: false,
    triggered_by: 'TMP_MESSAGE', // Store it as a tmp message
    community_id: options.currentTimelineId,
    thread_id: options.currentTimelineId,
    created_at: new Date()
      .toISOString(),
    updated_at: new Date()
      .toISOString(),
    modification_date: new Date()
      .toISOString(),
    id: '',
  }

  return item
}
